import { Typography, cn } from "@tosize/ui";
import { useEffect, useRef, useState } from "react";
import { useMedia } from "~/components/media/Provider";
import type { SectionProps } from "./Context";
import { MediaItem } from "./Item";
import styles from "./media.module.css";

export function MediaDialogOverview() {
	const { sortedSections } = useMedia();

	return (
		<div className="space-y-8">
			{sortedSections.map((section, key) => (
				<Section section={section} lastItem={key + 1 === sortedSections.length} key={section.id} />
			))}
		</div>
	);
}

function Section({ section, lastItem }: { section: SectionProps; lastItem: boolean }) {
	const { setOverviewSections, onMediaChange } = useMedia();
	const sectionRef = useRef<HTMLDivElement>(null);
	const [remaingingSpace, setRemaingingSpace] = useState<string>();

	useEffect(() => {
		const sectionCurrent = sectionRef?.current;
		const observer = new IntersectionObserver(
			([e]) => {
				if (e) {
					e.isIntersecting
						? setOverviewSections((prev) => [...prev, section.id])
						: setOverviewSections((prev) => prev.filter((item) => item !== section.id));
				}
			},
			{
				threshold: 1.0,
				root: sectionRef.current?.parentElement?.parentElement,
				rootMargin: "1px",
			},
		);

		sectionCurrent && observer.observe(sectionCurrent);

		return () => {
			sectionCurrent && observer.unobserve(sectionCurrent);
		};
	}, [section.id, setOverviewSections]);

	useEffect(() => {
		setRemaingingSpace(
			lastItem && sectionRef.current?.offsetHeight
				? `calc(calc(100dvh - var(--media-dialog-header-y, 0px) - ${sectionRef.current?.offsetHeight}px)`
				: "0px",
		);
	}, [lastItem]);

	return (
		<div
			ref={sectionRef}
			id={section.id}
			className={cn(styles.dialogTarget, "target")}
			style={{ marginBottom: remaingingSpace }}
		>
			<div className="mb-2 inline-flex items-center justify-center">
				<Typography variant="h3" component="span">
					{section.title}
				</Typography>
				<Typography variant="paragraph" component="span" className="ml-2 text-shades-medium">
					({section.items.length})
				</Typography>
			</div>

			<div className="grid grid-cols-2 gap-4 sm:grid-cols-3 xl:grid-cols-4">
				{section.items.map((item, index) => (
					<MediaItem
						item={item}
						onClick={() => onMediaChange({ type: "detail", id: section.id, index })}
						key={index}
					/>
				))}
			</div>
		</div>
	);
}
